import { ESCAPE, UP_ARROW } from '@angular/cdk/keycodes';
import { Overlay, OverlayConfig, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  isDevMode,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import { toBoolean } from '@ngx-simple/core/coercion';
import { ThemePalette } from '@ngx-simple/core/common-behaviors';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { NgClassInterface, SafeAny } from '@ngx-simple/core/types';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SimCalendarCellClassFunction } from './calendar-body.component';
import { SimCalendarView } from './calendar.component';
import { ExtractDateTypeFromSelection, SimDateSelectionModel } from './date-selection-model';
import { SimDatepickerContentBase } from './datepicker-content-base';
import { createMissingDateImplError } from './datepicker-errors';
import { DateFilterFn } from './datepicker-input-base';

/** 可与datepicker关联的表单控件 */
export interface SimDatepickerControl<D> {
  getStartValue(): D | null;
  getThemePalette(): ThemePalette;
  min: D | null;
  max: D | null;
  disabled: boolean;
  dateFilter: DateFilterFn<D>;
  picker: 'year' | 'month' | 'week' | 'date' | 'datetime';
  getConnectedOverlayOrigin(): ElementRef;
  stateChanges: Observable<void>;
}

/** 注入令牌，用于确定在日历打开时的滚动处理。 */
export const SIM_DATEPICKER_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>('SIM-datepicker-scroll-strategy');

export function SIM_DATEPICKER_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

/** datepicker下拉菜单在X轴上的可能位置 */
export type DatepickerDropdownPositionX = 'start' | 'end';

/** datepicker下拉菜单在Y轴上可能位置 */
export type DatepickerDropdownPositionY = 'above' | 'below';

export const SIM_DATEPICKER_SCROLL_STRATEGY_FACTORY_PROVIDER = {
  provide: SIM_DATEPICKER_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: SIM_DATEPICKER_SCROLL_STRATEGY_FACTORY
};

/** 日期选择器的基类 */
@Directive()
export abstract class SimDatepickerBase<C extends SimDatepickerControl<D>, S, D = ExtractDateTypeFromSelection<S>>
  implements OnDestroy, OnChanges {
  /** 最初打开日历的日期 */
  @Input()
  get startAt(): D | null {
    // 如果设置了显式的startAt，则从此处开始，否则从当前的任何位置开始所选值是。
    return this._startAt || (this._datepickerInput ? this._datepickerInput.getStartValue() : null);
  }
  set startAt(value: D | null) {
    this._startAt = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }
  private _startAt: D | null;

  /** 日历应该开始的视图 */
  @Input() startView: SimCalendarView = 'month';

  /** 是否展示“今天”按钮 */
  @Input() today: boolean;

  /** 在日期选择器的日历上使用的调色板 */
  @Input()
  get color(): ThemePalette {
    return this._color || (this._datepickerInput ? this._datepickerInput.getThemePalette() : undefined);
  }
  set color(value: ThemePalette) {
    this._color = value;
  }
  _color: ThemePalette = 'primary';

  /** 是否禁用日期选择器弹出窗口 */
  @Input()
  get disabled(): boolean {
    return this._disabled === undefined && this._datepickerInput ? this._datepickerInput.disabled : !!this._disabled;
  }
  set disabled(value: boolean) {
    const newValue = toBoolean(value);

    if (newValue !== this._disabled) {
      this._disabled = newValue;
      this._stateChanges.next(undefined);
    }
  }
  private _disabled: boolean;

  /**
   * 在多年视图中发出选定的年份。这并不意味着对所选日期的更改。
   */
  @Output() readonly yearSelected: EventEmitter<D> = new EventEmitter<D>();

  /**
   * 在年度视图中发出选定的月份。这并不意味着对所选日期的更改。
   */
  @Output() readonly monthSelected: EventEmitter<D> = new EventEmitter<D>();

  /**
   * 在年度视图中发出选定的周。这并不意味着对所选日期的更改。
   */
  @Output() readonly weekSelected: EventEmitter<D> = new EventEmitter<D>();

  /**
   * 在时间视图中发出选定的时间。这并不意味着对所选日期的更改。
   */
  @Output() readonly timeSelected: EventEmitter<D> = new EventEmitter<D>();

  /**
   * 在当前视图更改时发出
   */
  @Output() readonly viewChanged: EventEmitter<SimCalendarView> = new EventEmitter<SimCalendarView>(true);

  /** 要传递给日期选择器面板的类。 支持与ngClass相同的语法 */
  @Input() panelClass: NgClassInterface;

  /** 可用于将自定义CSS类添加到日期的函数 */
  @Input() dateClass: SimCalendarCellClassFunction<D>;

  /** 打开日期选择器面板后发出事件 */
  @Output() opened: EventEmitter<void> = new EventEmitter<void>();

  /** 关闭日期选择器面板后发出事件 */
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  /** 与此datepicker关联的<input> */
  _datepickerInput: C;

  /** 当日期选择器的状态更改时发出 */
  readonly _stateChanges = new Subject<void>();

  private _scrollStrategy: () => ScrollStrategy;

  private _document: Document;

  /** 日期面板是否打开 */
  private _panelOpen: boolean;

  private _inputStateChanges = Subscription.EMPTY;

  /** 打开日期选择器之前聚焦的元素 */
  private _focusedElementBeforeOpen: HTMLElement | null = null;

  /** 当日历作为弹出窗口打开时，对覆盖层的引用 */
  private _popupRef: OverlayRef | null;

  /** 对在弹出模式中实例化的组件的引用 */
  private _popupComponentRef: ComponentRef<SimDatepickerContentBase<S, D>> | null;

  constructor(
    private _overlay: Overlay,
    private _ngZone: NgZone,
    protected _viewContainerRef: ViewContainerRef,
    @Inject(SIM_DATEPICKER_SCROLL_STRATEGY) scrollStrategy: any,
    @Optional() protected _dateAdapter: SimDateAdapter<D>,
    @Optional() @Inject(DOCUMENT) document: SafeAny,
    private _model: SimDateSelectionModel<S, D>
  ) {
    if (!this._dateAdapter && isDevMode) {
      throw createMissingDateImplError('simDateAdapter');
    }
    this._document = document;
    this._scrollStrategy = scrollStrategy;
  }

  ngOnChanges(changes: SimpleChanges) {
    this._stateChanges.next(undefined);
  }

  ngOnDestroy() {
    this._destroyPopup();
    this.close();
    this._inputStateChanges.unsubscribe();
    this._stateChanges.complete();
  }

  /** 在多年视图中发出选定的年份 */
  _selectYear(normalizedYear: D): void {
    this.yearSelected.emit(normalizedYear);
  }

  /** 在年度视图中发出选定的月份 */
  _selectMonth(normalizedMonth: D): void {
    this.monthSelected.emit(normalizedMonth);
  }

  /** 视图发出改变 */
  _viewChanged(view: SimCalendarView): void {
    this.viewChanged.emit(view);
  }

  /** 可选择的最小日期 */
  _getMinDate(): D | null {
    return this._datepickerInput && this._datepickerInput.min;
  }

  /** 可选择的最大日期 */
  _getMaxDate(): D | null {
    return this._datepickerInput && this._datepickerInput.max;
  }

  /** 可选择过滤后的日期 */
  _getDateFilter(): DateFilterFn<D> {
    return this._datepickerInput && this._datepickerInput.dateFilter;
  }

  /**
   * 使用此日期选择器注册simDatepickerInput
   * @param input 要向此datepicker注册的 simDatepickerInput
   * @returns simDatepickerInput应该连接到的选择模型
   */
  _registerInput(input: C): SimDateSelectionModel<S, D> {
    if (this._datepickerInput && isDevMode) {
      throw Error('A SimDatepicker can only be associated with a single input.');
    }
    this._inputStateChanges.unsubscribe();
    this._datepickerInput = input;
    this._inputStateChanges = input.stateChanges.subscribe(() => this._stateChanges.next(undefined));
    return this._model;
  }

  open() {
    if (this._panelOpen || this.disabled) {
      return;
    }
    if (!this._datepickerInput && isDevMode) {
      throw Error('Attempted to open an SimDatepicker with no associated input.');
    }
    if (this._document) {
      this._focusedElementBeforeOpen = (this._document.activeElement as unknown) as HTMLElement;
    }
    this._initStartView();
    this._openAsPopup();
    this._panelOpen = true;
    this.opened.emit();
  }

  close() {
    if (!this._panelOpen) {
      return;
    }
    if (this._popupComponentRef && this._popupRef) {
      const instance = this._popupComponentRef.instance;
      instance._startExitAnimation();
      instance._animationDone.pipe(take(1)).subscribe(() => this._destroyPopup());
    }

    const completeClose = () => {
      if (this._panelOpen) {
        this._panelOpen = false;
        this.closed.emit();
        this._focusedElementBeforeOpen = null;
      }
    };

    if (this._focusedElementBeforeOpen && typeof this._focusedElementBeforeOpen.focus === 'function') {
      this._focusedElementBeforeOpen.focus();
      const timer = setTimeout(() => {
        completeClose();
        clearTimeout(timer);
      });
    } else {
      completeClose();
    }
  }

  protected abstract createPortal(): ComponentPortal<SafeAny>;

  /** 根据picker初始化开始视图 */
  private _initStartView() {
    switch (this._datepickerInput.picker) {
      case 'year':
        this.startView = 'multi-year';
        break;
      case 'month':
        this.startView = 'year';
        break;
      case 'week':
        this.startView = 'week';
        break;
      case 'date':
        this.startView = 'month';
        break;
      case 'datetime':
        this.startView = 'month';
        break;
      default:
        break;
    }
  }

  private _openAsPopup() {
    const portal = this.createPortal();
    this._destroyPopup();
    this._createPopup();
    this._popupComponentRef = this._popupRef.attach(portal);
    this._popupComponentRef.instance.datepicker = this;
    this._popupComponentRef.instance.color = this.color;

    // 一旦日历呈现，就更新位置。
    this._ngZone.onStable
      .asObservable()
      .pipe(take(1))
      .subscribe(() => {
        this._popupRef.updatePosition();
      });
  }

  private _createPopup() {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._datepickerInput.getConnectedOverlayOrigin())
      .withTransformOriginOn('.sim-datepicker-content')
      .withFlexibleDimensions(false)
      .withViewportMargin(8)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top'
        },
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'end',
          overlayY: 'bottom'
        }
      ]);

    const overlayConfig = new OverlayConfig({
      positionStrategy,
      hasBackdrop: true,
      disposeOnNavigation: true,
      backdropClass: 'sim-overlay-transparent-backdrop',
      scrollStrategy: this._scrollStrategy(),
      panelClass: 'sim-datepicker-popup'
    });

    this._popupRef = this._overlay.create(overlayConfig);
    this._popupRef.overlayElement.setAttribute('role', 'dialog');

    merge(
      this._popupRef.backdropClick(),
      this._popupRef.detachments(),
      this._popupRef.keydownEvents().pipe(
        filter(event => {
          // tslint:disable-next-line: deprecation
          return event.keyCode === ESCAPE || (this._datepickerInput && event.altKey && event.keyCode === UP_ARROW);
        })
      )
    ).subscribe((event: MouseEvent) => {
      if (event) {
        event.preventDefault();
      }

      this.close();
    });
  }

  /** 销毁当前弹出选择器面板浮层 */
  private _destroyPopup() {
    if (this._popupRef) {
      this._popupRef.dispose();
      this._popupRef = this._popupComponentRef = null;
    }
  }
}
